import { useDraggable } from '@dnd-kit/core';
import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@/shared/ui';

import {
  PALETTE_DRAG_TYPES,
  PaletteDragType,
  templateEditorMessages,
} from '../../config';

import styles from './EditorToolbar.module.scss';

type EditorToolbarProps = {
  allowOptions: boolean;
  hasSections: boolean;
  hasAnyOptions: boolean;
  className?: string;
  onAddSection: () => void;
  onAddStrictOption: () => void;
  onAddField: () => void;
  onAddFieldPointerDownCapture?: () => void;
};

type DraggableToolbarButtonProps = {
  dragType: PaletteDragType;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  keepsEditorFocus?: boolean;
  onClick: () => void;
  onPointerDownCapture?: () => void;
};

const DraggableToolbarButton: FC<DraggableToolbarButtonProps> = (props) => {
  const {
    dragType,
    children,
    className,
    disabled,
    keepsEditorFocus,
    onClick,
    onPointerDownCapture,
  } = props;

  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragType,
    disabled,
  });

  return (
    <Button
      ref={setNodeRef}
      variant="secondary"
      size="medium"
      icon="plus"
      disabled={disabled}
      className={cn(
        styles.dragButton,
        isDragging && styles.dragging,
        className,
      )}
      style={
        transform
          ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
          : undefined
      }
      onPointerDownCapture={onPointerDownCapture}
      // A focused option editor drops its caret insertion target on blur, so
      // the button must not take focus away from it.
      onMouseDown={(event) => keepsEditorFocus && event.preventDefault()}
      // Only the pointer starts a drag: the keyboard listeners would turn
      // Enter/Space into a keyboard drag and take the click insertion away
      // from keyboard users.
      onPointerDown={(event) => listeners?.onPointerDown?.(event)}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

export const EditorToolbar: FC<EditorToolbarProps> = (props) => {
  const {
    allowOptions,
    hasSections,
    hasAnyOptions,
    className,
    onAddSection,
    onAddStrictOption,
    onAddField,
    onAddFieldPointerDownCapture,
  } = props;

  const { formatMessage } = useIntl();

  return (
    <div
      className={cn(styles.container, className)}
      data-component="template-editor-toolbar"
    >
      <DraggableToolbarButton
        dragType={PALETTE_DRAG_TYPES.SECTION}
        onClick={onAddSection}
      >
        {formatMessage(templateEditorMessages.addSection)}
      </DraggableToolbarButton>
      {allowOptions && (
        <>
          <DraggableToolbarButton
            dragType={PALETTE_DRAG_TYPES.OPTION}
            disabled={!hasSections}
            onClick={onAddStrictOption}
          >
            {formatMessage(templateEditorMessages.addStrictOption)}
          </DraggableToolbarButton>
          <DraggableToolbarButton
            dragType={PALETTE_DRAG_TYPES.FIELD}
            disabled={!hasAnyOptions}
            keepsEditorFocus
            onClick={onAddField}
            onPointerDownCapture={onAddFieldPointerDownCapture}
          >
            {formatMessage(templateEditorMessages.addSlot)}
          </DraggableToolbarButton>
        </>
      )}
    </div>
  );
};
